import React, { useState, useEffect } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import axios from 'axios';
import PropTypes from "prop-types";
import { Table, Badge, Button, Modal, Form, Accordion, Card } from 'react-bootstrap';
import AddFumigation from './PopUps/Add/AddFumigation';
import EditFumigation from './PopUps/Edit/EditFumigation';
import * as Icon from 'react-feather';
import { canPageAction } from '../../config/roleConfig';
import DeactivateConfirmModal from '../Common/DeactivateConfirmModal';

function Fumigation(props)  {
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();
    const currentUser = authUser();
    const canRead = canPageAction(currentUser, 'operations.fumigation', 'read');
    const canWrite = canPageAction(currentUser, 'operations.fumigation', 'write');
    const canDelete = canPageAction(currentUser, 'operations.fumigation', 'delete');

    const [data, setData] = useState([]);
    const [inventoryOptions, setInventoryOptions] = useState([]);
    const [selectedInventoryId, setSelectedInventoryId] = useState('');
    const [expandedFumigation, setExpandedFumigation] = useState(null);
    const [fumigationToDeactivate, setFumigationToDeactivate] = useState(null);
    const [deactivatingFumigation, setDeactivatingFumigation] = useState(false);
    const apiUrl = process.env.REACT_APP_URL;

    const get_all_data = (inventoryId = '') => {
        let url = apiUrl + '/services/fumigation';
        if (inventoryId) {
            url += `?inventory_id=${inventoryId}`;
        }

        axios({
            method:'GET',
            url: url,
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setData(response.data.msg.Fumigations || []);
        })
        .catch(error => {
            console.error('Error fetching fumigation data:', error);
            alert('Error al cargar datos de fumigación');
        });
    }

    const get_inventory_options = () => {
        axios({
            method: 'GET',
            url: apiUrl + '/units/get_all_units',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setInventoryOptions(response.data.msg.Units || []);
        })
        .catch(error => {
            console.error('Error fetching inventory options:', error);
        });
    }

    useEffect(() => {
        get_inventory_options();
    }, []);

    useEffect(() => {
        if (inventoryOptions.length > 0) {
            const firstUnitId = inventoryOptions[0].id;
            setSelectedInventoryId(firstUnitId);
            get_all_data(firstUnitId);
        }
    }, [inventoryOptions]);

    const handleInventoryChange = (e) => {
        const inventoryId = e.target.value;
        setSelectedInventoryId(inventoryId);
        get_all_data(inventoryId);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return <Badge bg="success">Completado</Badge>;
            case 'scheduled':
                return <Badge bg="warning">Programado</Badge>;
            case 'cancelled':
                return <Badge bg="danger">Cancelado</Badge>;
            default:
                return <Badge bg="light">{status}</Badge>;
        }
    };

    const getPaymentBadge = (isPaid) => {
        return isPaid ?
            <Badge bg="success">Pagado</Badge> :
            <Badge bg="warning">Pendiente</Badge>;
    };

    const toggleFumigationDetails = (fumigationId) => {
        setExpandedFumigation(expandedFumigation === fumigationId ? null : fumigationId);
    };

    const deleteFumigation = (fumigation) => {
        if (!fumigation) {
            return;
        }

        setDeactivatingFumigation(true);
        axios({
            method: 'DELETE',
            url: apiUrl + `/services/fumigation/${fumigation.id}`,
            headers: {
                Authorization: authHeader()
            }
        }).then(() => {
            setFumigationToDeactivate(null);
            refreshData();
        }).catch(error => {
            alert(error.response?.data?.error || error.response?.data?.msg || 'No se pudo desactivar la fumigación');
            console.log(error);
        }).finally(() => {
            setDeactivatingFumigation(false);
        });
    };

    const refreshData = () => {
        get_all_data(selectedInventoryId);
    };

    return (
        <>
        <div className="card mb-4">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between">
                    <h5 className="card-title">Registros de Fumigación</h5>
                    {canWrite && (
                        <AddFumigation
                            setData={refreshData}
                            inventoryOptions={inventoryOptions}
                        />
                    )}
                </div>

                <div className="mb-3 d-flex align-items-center">
                    <Form.Group className="d-flex align-items-center">
                        <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Filtrar por Unidad:</Form.Label>
                        <Form.Select
                            value={selectedInventoryId}
                            onChange={handleInventoryChange}
                            style={{width: '250px'}}
                        >
                            {inventoryOptions.map(inventory => (
                                <option key={inventory.id} value={inventory.id}>
                                    {inventory.inventory_name || inventory.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>

                <div>
                    <Table className="m-0" responsive>
                        <thead>
                            <tr>
                                <th>Unidad</th>
                                <th>Fecha/Hora Servicio</th>
                                <th>Estado</th>
                                <th>Proveedor</th>
                                <th>Licencia Sanitaria</th>
                                <th>Costo Total</th>
                                <th>Estado Pago</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(fumigation => (
                                <React.Fragment key={fumigation.id}>
                                    <tr>
                                        <td>
                                            <div>
                                                <strong>{fumigation.inventory_name}</strong>
                                                {fumigation.inventory_plates && (
                                                    <div className="text-muted small">{fumigation.inventory_plates}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>{fumigation.service_datetime}</td>
                                        <td>{getStatusBadge(fumigation.status)}</td>
                                        <td>{fumigation.provider || 'No asignado'}</td>
                                        <td>{fumigation.sanitary_license || 'N/A'}</td>
                                        <td>${parseFloat(fumigation.total_cost || 0).toFixed(2)}</td>
                                        <td>{getPaymentBadge(fumigation.is_paid)}</td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                {canRead && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => toggleFumigationDetails(fumigation.id)}
                                                    >
                                                        <Icon.Eye size={14} />
                                                    </Button>
                                                )}
                                                {canWrite && (
                                                    <EditFumigation
                                                        setData={refreshData}
                                                        fumigationInfo={fumigation}
                                                        inventoryOptions={inventoryOptions}
                                                    />
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => setFumigationToDeactivate(fumigation)}
                                                    >
                                                        <Icon.Trash2 size={14} />
                                                    </Button>
                                                )}
                                                {!canRead && !canWrite && !canDelete && (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedFumigation === fumigation.id && (
                                        <tr>
                                            <td colSpan="8">
                                                <div className="p-3 bg-light">
                                                    <div className="row">
                                                        <div className="col-md-4">
                                                            <p><strong>Unidad:</strong> {fumigation.inventory_name}</p>
                                                            <p><strong>Placas:</strong> {fumigation.inventory_plates || 'N/A'}</p>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <p><strong>Proveedor:</strong> {fumigation.provider || 'N/A'}</p>
                                                            <p><strong>Licencia:</strong> {fumigation.sanitary_license || 'N/A'}</p>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <p><strong>Costo:</strong> ${parseFloat(fumigation.total_cost || 0).toFixed(2)}</p>
                                                            <p><strong>Pago:</strong> {fumigation.is_paid ? 'Pagado' : 'Pendiente'}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {data.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="text-center text-muted">
                                        No hay registros de fumigación para esta unidad
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
        <DeactivateConfirmModal
            show={Boolean(fumigationToDeactivate)}
            title="Desactivar Fumigación"
            itemLabel={fumigationToDeactivate?.inventory_name}
            confirming={deactivatingFumigation}
            onCancel={() => !deactivatingFumigation && setFumigationToDeactivate(null)}
            onConfirm={() => deleteFumigation(fumigationToDeactivate)}
        />
        </>
    );
}

export default Fumigation;
